import React, { useState, useEffect } from 'react';

const PaymentManager = ({ document, onPaymentUpdate, onClose }) => {
  const [montantPaye, setMontantPaye] = useState(document.montant_paye || 0);
  const [commentaire, setCommentaire] = useState('');
  const [loading, setLoading] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);

  // Sprint 4: Charger les informations de paiement spécifiques au type de document
  useEffect(() => {
    const fetchPaymentInfo = async () => {
      try {
        setLoadingInfo(true);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${document.id}/payment-info`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const info = await response.json();
          setPaymentInfo(info);
          
          // Si les paiements sont désactivés pour ce type de document, fermer automatiquement
          if (!info.payment_enabled) {
            alert(`La gestion des paiements n'est pas disponible pour les ${info.document_type}s`);
            onClose();
            return;
          }
        } else {
          console.error('Erreur lors du chargement des informations de paiement');
        }
      } catch (error) {
        console.error('Erreur:', error);
      } finally {
        setLoadingInfo(false);
      }
    };

    fetchPaymentInfo();
  }, [document.id, document.type, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Appel à l'API pour mettre à jour le paiement
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${document.id}/payment`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          montant_paye: montantPaye,
          commentaire
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erreur lors de la mise à jour du paiement');
      }

      const updatedDocument = await response.json();
      onPaymentUpdate(updatedDocument);
      onClose();
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.message || 'Erreur lors de la mise à jour du paiement');
    } finally {
      setLoading(false);
    }
  };

  // Affichage de chargement
  if (loadingInfo) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <span className="ml-2">Chargement...</span>
          </div>
        </div>
      </div>
    );
  }

  // Si les informations de paiement ne sont pas disponibles
  if (!paymentInfo) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <h3 className="text-lg font-semibold text-red-600 mb-4">
            Erreur
          </h3>
          <p>Impossible de charger les informations de paiement.</p>
          <div className="flex justify-end mt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    );
  }

  const resteAPayer = paymentInfo.total_ttc - montantPaye;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Gestion des Paiements
          {paymentInfo.document_type === 'facture' && ' - Encaissement Client'}
          {paymentInfo.document_type === 'bon_commande' && ' - Paiement Transporteur'}
        </h3>
        
        <div className="space-y-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Informations du Document</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Référence:</span>
                <span className="font-medium">{paymentInfo.reference}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{paymentInfo.entity_label}:</span>
                <span className="font-medium">{paymentInfo.entity_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total TTC:</span>
                <span className="font-medium">{paymentInfo.total_ttc.toLocaleString()} CFA</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{paymentInfo.montant_label}:</span>
                <span className="font-medium">{paymentInfo.montant_paye.toLocaleString()} CFA</span>
              </div>
              <div className="flex justify-between border-t pt-1">
                <span className="text-gray-600">{paymentInfo.reste_label}:</span>
                <span className="font-medium text-red-600">{paymentInfo.reste_a_payer.toLocaleString()} CFA</span>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {paymentInfo.montant_total_label}
            </label>
            <input
              type="number"
              value={montantPaye}
              onChange={(e) => setMontantPaye(parseFloat(e.target.value) || 0)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              min="0"
              max={paymentInfo.total_ttc}
              step="0.01"
              placeholder="Montant en CFA"
            />
            <p className="text-xs text-gray-500 mt-1">
              Maximum: {paymentInfo.total_ttc.toLocaleString()} CFA
            </p>
          </div>

          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-700">{paymentInfo.nouveau_reste_label}:</span>
              <span className={`font-bold ${resteAPayer <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {resteAPayer.toLocaleString()} CFA
              </span>
            </div>
            {resteAPayer <= 0 && (
              <p className="text-xs text-green-600 mt-1">
                ✓ {paymentInfo.document_type === 'facture' ? 'Document entièrement encaissé' : 'Document entièrement payé'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Commentaire (optionnel)
            </label>
            <textarea
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              rows={3}
              placeholder={`Commentaire sur ${paymentInfo.document_type === 'facture' ? "l'encaissement" : 'le paiement'}`}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Mise à jour...' : `Mettre à jour ${paymentInfo.document_type === 'facture' ? "l'encaissement" : 'le paiement'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Composant pour afficher le statut de paiement - Adapté Sprint 4
const PaymentStatus = ({ document }) => {
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPaymentInfo = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${document.id}/payment-info`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const info = await response.json();
          setPaymentInfo(info);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des informations de paiement:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentInfo();
  }, [document.id]);

  if (loading) {
    return (
      <div className="space-y-2">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-24"></div>
          <div className="space-y-1 mt-2">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!paymentInfo || !paymentInfo.payment_enabled) {
    return (
      <div className="space-y-2">
        <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <span className="mr-1">📄</span>
          {paymentInfo?.document_type === 'devis' ? 'Devis - Pas de paiement' : 'N/A'}
        </div>
      </div>
    );
  }

  const paiementComplet = paymentInfo.reste_a_payer <= 0;
  const paiementPartiel = paymentInfo.montant_paye > 0 && paymentInfo.reste_a_payer > 0;
  const aucunPaiement = paymentInfo.montant_paye === 0;

  let statusColor = 'bg-gray-100 text-gray-800';
  let statusText = paymentInfo.document_type === 'facture' ? 'Non encaissé' : 'Non payé';
  let statusIcon = '⏳';

  if (paiementComplet) {
    statusColor = 'bg-green-100 text-green-800';
    statusText = paymentInfo.document_type === 'facture' ? 'Encaissé' : 'Payé';
    statusIcon = '✅';
  } else if (paiementPartiel) {
    statusColor = 'bg-yellow-100 text-yellow-800';
    statusText = paymentInfo.document_type === 'facture' ? 'Partiellement encaissé' : 'Partiellement payé';
    statusIcon = '';
  }

  return (
    <div className="space-y-2">
      <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
        <span className="mr-1">{statusIcon}</span>
        {statusText}
      </div>
      
      <div className="text-sm space-y-1">
        <div className="flex justify-between">
          <span className="text-gray-600">Total TTC:</span>
          <span className="font-medium">{paymentInfo.total_ttc.toLocaleString()} CFA</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{paymentInfo.montant_label.replace('Montant déjà', '').trim()}:</span>
          <span className="font-medium">{paymentInfo.montant_paye.toLocaleString()} CFA</span>
        </div>
        <div className="flex justify-between border-t pt-1">
          <span className="text-gray-600">{paymentInfo.reste_label}:</span>
          <span className={`font-medium ${paymentInfo.reste_a_payer <= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {paymentInfo.reste_a_payer.toLocaleString()} CFA
          </span>
        </div>
      </div>
      
      {/* Barre de progression */}
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className="bg-green-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${(paymentInfo.montant_paye / paymentInfo.total_ttc) * 100}%` }}
        />
      </div>
      <p className="text-xs text-gray-500">
        {((paymentInfo.montant_paye / paymentInfo.total_ttc) * 100).toFixed(1)}% {paymentInfo.document_type === 'facture' ? 'encaissé' : 'payé'}
      </p>
    </div>
  );
};

export { PaymentManager, PaymentStatus };
export default PaymentManager;